/**
 * Validation shared by every FileDropZone.
 *
 * A native `<input type="file" accept=…>` only filters the OS picker — a drop
 * bypasses it entirely, and most pickers let the user switch to "All files".
 * These helpers apply the same `accept` / size rules to both paths so a zone
 * rejects exactly what its input would have refused.
 *
 * Customer review 11 Sep (user request: drag and drop on every upload).
 */
import { describeFileTypes, fileTypeRefusedMessage } from '../../lib/fileTypes';

/** A file the zone refused, with the reason shown to the user. */
export interface FileRejection {
  file: File;
  reason: string;
}

/**
 * Split an `accept` attribute into lower-cased tokens.
 *
 * @param accept - Value of the input's accept attribute (e.g. ".pdf,image/*")
 * @returns The individual tokens; empty when anything is accepted
 */
export function parseAccept(accept?: string): string[] {
  if (!accept) return [];
  return accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Whether a file satisfies an `accept` attribute, using the browser's rules:
 * `.ext` matches the file name, `type/*` the MIME family, `type/sub` exactly.
 *
 * @param file - The candidate file
 * @param accept - The accept attribute; empty/undefined accepts everything
 * @returns True when the file would be offered by the picker
 */
export function fileMatchesAccept(file: File, accept?: string): boolean {
  const tokens = parseAccept(accept);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const mime = (file.type || '').toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return mime.startsWith(token.slice(0, -1));
    return mime === token;
  });
}

/**
 * Human-readable list of the accepted types, for rejection messages.
 *
 * CR-7460: plain words, the same as the server's refusal message.
 *
 * @param accept - The accept attribute
 * @returns e.g. "PDF, Word or image"
 */
export function describeAccept(accept?: string): string {
  return describeFileTypes(parseAccept(accept));
}

/**
 * Format a byte limit the way the upload copy already does ("10MB", "500KB").
 *
 * @param bytes - The limit in bytes
 * @returns A short label
 */
export function formatFileLimit(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${Number.isInteger(mb) ? mb : mb.toFixed(1)}MB`;
  return `${Math.round(bytes / 1024)}KB`;
}

/**
 * Split candidate files into accepted and rejected against a zone's rules.
 *
 * @param files - Files from a drop or the picker
 * @param rules - The zone's accept attribute and per-file size cap
 * @returns The files to hand to the caller, and the refusals to report
 */
export function partitionFiles(
  files: File[],
  rules: { accept?: string; maxSize?: number },
): { accepted: File[]; rejected: FileRejection[] } {
  const accepted: File[] = [];
  const rejected: FileRejection[] = [];
  for (const file of files) {
    if (!fileMatchesAccept(file, rules.accept)) {
      rejected.push({ file, reason: fileTypeRefusedMessage(parseAccept(rules.accept)) });
    } else if (rules.maxSize && file.size > rules.maxSize) {
      rejected.push({ file, reason: `larger than the ${formatFileLimit(rules.maxSize)} limit` });
    } else {
      accepted.push(file);
    }
  }
  return { accepted, rejected };
}

/**
 * True when a drag carries files from the OS (as opposed to text, links or an
 * in-app draggable such as a dnd-kit card, which the zones must ignore).
 *
 * @param dataTransfer - The drag event's dataTransfer
 * @returns Whether the drag contains files
 */
export function isFileDrag(dataTransfer: DataTransfer | null): boolean {
  return !!dataTransfer && Array.from(dataTransfer.types || []).includes('Files');
}
